// src/admin/pros/ProsTable.jsx
import React, { useState, useEffect } from "react";

const STATUS_STYLES = {
  active: { dot: "#059669", text: "#059669" },
  pending: { dot: "#94A3B8", text: "#64748B" },
  suspended: { dot: "#DC2626", text: "#DC2626" },
};

function relativeTime(isoString) {
  const diff = Math.floor((Date.now() - new Date(isoString)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return `${diff}d ago`;
}

function avatarBg(name) {
  const palette = ["#EEF2FF", "#F0FDF4", "#FEF3C7", "#FEE2E2", "#E0F2FE", "#FCE7F3", "#F5F3FF"];
  return palette[name.charCodeAt(0) % palette.length];
}

function avatarColor(name) {
  const palette = ["#4338CA", "#16A34A", "#D97706", "#DC2626", "#0369A1", "#BE185D", "#7C3AED"];
  return palette[name.charCodeAt(0) % palette.length];
}

function initials(name) {
  return name.split(" ").map(n => n[0]).slice(0, 2).join("");
}

function DropdownItem({ label, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        padding: "10px 16px",
        textAlign: "left",
        border: "none",
        background: "transparent",
        fontSize: 13,
        fontWeight: 500,
        color: danger ? "#DC2626" : "#334155",
        cursor: "pointer",
        transition: "background 0.1s ease"
      }}
      onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      {label}
    </button>
  );
}

export default function ProsTable({
  pros,
  allPros,
  selectedIds,
  onRowClick,
  onSelect,
  onSelectAll,
  onSort,
  sortField,
  sortDir,
  currentPage,
  onPageChange,
  pageSize,
  totalPages,
  loading,
  error,
  onRetry,
  onClearFilters,
  onApprovePro,
  onSuspendPro,
  onMessagePro,
  onPayoutReview
}) {
  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".dropdown-container")) {
        setOpenDropdownId(null);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const allSelectedOnPage = pros.length > 0 && pros.every(p => selectedIds.has(p.id));
  const someSelectedOnPage = pros.some(p => selectedIds.has(p.id));

  const handleSelectAllClick = (e) => {
    onSelectAll(e.target.checked);
  };

  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, allPros.length);

  if (loading) {
    return (
      <div style={{
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #F1F5F9",
        overflow: "hidden"
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <thead>
            <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E2E8F0", height: 44 }}>
              <th style={{ width: 44, padding: "0 12px" }} />
              <th style={{ width: 220, textAlign: "left", padding: "0 12px" }}>Name</th>
              <th style={{ width: 170, textAlign: "left", padding: "0 12px" }}>Contact</th>
              <th style={{ width: 120, textAlign: "left", padding: "0 12px" }}>Category</th>
              <th style={{ width: 90, textAlign: "right", padding: "0 12px" }}>Jobs</th>
              <th style={{ width: 80, textAlign: "right", padding: "0 12px" }}>Rating</th>
              <th style={{ width: 120, textAlign: "right", padding: "0 12px" }}>Earnings</th>
              <th style={{ width: 110, textAlign: "left", padding: "0 12px" }}>Status</th>
              <th style={{ width: 110, textAlign: "left", padding: "0 12px" }}>Last Active</th>
              <th style={{ width: 140, textAlign: "right", padding: "0 12px" }}>Actions</th>
             </tr>
          </thead>
          <tbody>
            {[...Array(8)].map((_, i) => (
              <tr key={i} style={{ height: 56, borderBottom: "1px solid #F8FAFC" }}>
                {[...Array(10)].map((_, j) => (
                  <td key={j} style={{ padding: "0 12px" }}>
                    <div style={{
                      height: 14,
                      borderRadius: 4,
                      background: "linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.4s infinite",
                      width: j === 0 ? 20 : "70%"
                    }} />
                   </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <style>{`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #F1F5F9",
        padding: 60,
        textAlign: "center"
      }}>
        <p style={{
          fontSize: 15,
          color: "#DC2626",
          marginBottom: 12,
          fontFamily: "'Plus Jakarta Sans', sans-serif"
        }}>
          Failed to load pros: {error}
        </p>
        <button
          onClick={onRetry}
          style={{
            padding: "8px 20px",
            background: "#0F172A",
            border: "none",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            color: "#fff",
            cursor: "pointer",
            fontFamily: "'Plus Jakarta Sans', sans-serif"
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (pros.length === 0) {
    return (
      <div style={{
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #F1F5F9",
        padding: 60,
        textAlign: "center"
      }}>
        <p style={{
          fontSize: 15,
          color: "#94A3B8",
          marginBottom: 12,
          fontFamily: "'Plus Jakarta Sans', sans-serif"
        }}>
          No pros found
        </p>
        <button
          onClick={onClearFilters}
          style={{
            padding: "8px 20px",
            background: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            color: "#64748B",
            cursor: "pointer",
            fontFamily: "'Plus Jakarta Sans', sans-serif"
          }}
        >
          Clear filters
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #F1F5F9",
        overflow: "auto"
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <thead>
            <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E2E8F0", height: 44 }}>
              <th style={{ width: 44, padding: "0 12px", textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={allSelectedOnPage}
                  ref={input => {
                    if (input) input.indeterminate = someSelectedOnPage && !allSelectedOnPage;
                  }}
                  onChange={handleSelectAllClick}
                  style={{ cursor: "pointer" }}
                />
              </th>
              <th style={{ width: 220, textAlign: "left", padding: "0 12px" }}>
                <button
                  onClick={() => onSort("name")}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#94A3B8",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}
                >
                  Name
                  {sortField === "name" && (sortDir === 1 ? " ↑" : " ↓")}
                  {sortField !== "name" && " ↕"}
                </button>
              </th>
              <th style={{ width: 170, textAlign: "left", padding: "0 12px" }}>
                <span style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#94A3B8",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em"
                }}>
                  Contact
                </span>
              </th>
              <th style={{ width: 120, textAlign: "left", padding: "0 12px" }}>
                <button
                  onClick={() => onSort("category")}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#94A3B8",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}
                >
                  Category
                  {sortField === "category" && (sortDir === 1 ? " ↑" : " ↓")}
                  {sortField !== "category" && " ↕"}
                </button>
              </th>
              <th style={{ width: 90, textAlign: "right", padding: "0 12px" }}>
                <button
                  onClick={() => onSort("jobsCompleted")}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#94A3B8",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 4,
                    width: "100%",
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}
                >
                  Jobs
                  {sortField === "jobsCompleted" && (sortDir === 1 ? " ↑" : " ↓")}
                  {sortField !== "jobsCompleted" && " ↕"}
                </button>
              </th>
              <th style={{ width: 80, textAlign: "right", padding: "0 12px" }}>
                <button
                  onClick={() => onSort("rating")}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#94A3B8",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 4,
                    width: "100%",
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}
                >
                  Rating
                  {sortField === "rating" && (sortDir === 1 ? " ↑" : " ↓")}
                  {sortField !== "rating" && " ↕"}
                </button>
              </th>
              <th style={{ width: 120, textAlign: "right", padding: "0 12px" }}>
                <button
                  onClick={() => onSort("earnings")}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#94A3B8",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 4,
                    width: "100%",
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}
                >
                  Earnings
                  {sortField === "earnings" && (sortDir === 1 ? " ↑" : " ↓")}
                  {sortField !== "earnings" && " ↕"}
                </button>
              </th>
              <th style={{ width: 110, textAlign: "left", padding: "0 12px" }}>
                <button
                  onClick={() => onSort("status")}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#94A3B8",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}
                >
                  Status
                  {sortField === "status" && (sortDir === 1 ? " ↑" : " ↓")}
                  {sortField !== "status" && " ↕"}
                </button>
              </th>
              <th style={{ width: 110, textAlign: "left", padding: "0 12px" }}>
                <button
                  onClick={() => onSort("lastActive")}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#94A3B8",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}
                >
                  Last Active
                  {sortField === "lastActive" && (sortDir === 1 ? " ↑" : " ↓")}
                  {sortField !== "lastActive" && " ↕"}
                </button>
              </th>
              <th style={{ width: 140, textAlign: "right", padding: "0 12px" }}>
                <span style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#94A3B8",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em"
                }}>
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pros.map(pro => (
              <tr
                key={pro.id}
                onClick={() => onRowClick(pro)}
                style={{
                  height: 56,
                  borderBottom: "1px solid #F8FAFC",
                  cursor: "pointer",
                  background: selectedIds.has(pro.id) ? "#F0FDF4" : "transparent"
                }}
                onMouseEnter={e => {
                  if (!selectedIds.has(pro.id)) e.currentTarget.style.background = "#F8FAFC";
                }}
                onMouseLeave={e => {
                  if (!selectedIds.has(pro.id)) e.currentTarget.style.background = "transparent";
                }}
              >
                <td style={{ padding: "0 12px", textAlign: "center" }} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(pro.id)}
                    onChange={(e) => onSelect(pro.id, e.target.checked)}
                    style={{ cursor: "pointer" }}
                  />
                </td>
                <td style={{ padding: "0 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: avatarBg(pro.name),
                      color: avatarColor(pro.name),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0
                    }}>
                      {initials(pro.name)}
                    </div>
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0F172A", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        {pro.name}
                      </div>
                      <div style={{ fontSize: 11, color: "#94A3B8", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        {pro.id}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: "0 12px" }}>
                  <div style={{ fontSize: 13, color: "#334155", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {pro.phone}
                  </div>
                  <div style={{ fontSize: 11, color: "#94A3B8", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {pro.email}
                  </div>
                </td>
                <td style={{ padding: "0 12px", fontSize: 13, color: "#334155", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {pro.category}
                </td>
                <td style={{ padding: "0 12px", textAlign: "right", fontSize: 13, fontWeight: 600, color: "#334155", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {pro.jobsCompleted}
                </td>
                <td style={{ padding: "0 12px", textAlign: "right" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="#F59E0B" stroke="#F59E0B" strokeWidth="2">
                      <polygon points="12 17.27 18.18 21 16.54 13.97 22 9.24 14.81 8.63 12 2 9.19 8.63 2 9.24 7.46 13.97 5.82 21 12 17.27" />
                    </svg>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {pro.rating}
                    </span>
                  </div>
                </td>
                <td style={{ padding: "0 12px", textAlign: "right" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    KES {pro.earnings.toLocaleString()}
                  </span>
                </td>
                <td style={{ padding: "0 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: STATUS_STYLES[pro.status].dot,
                      display: "inline-block",
                      flexShrink: 0
                    }} />
                    <span style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: STATUS_STYLES[pro.status].text,
                      fontFamily: "'Plus Jakarta Sans', sans-serif"
                    }}>
                      {pro.status.charAt(0).toUpperCase() + pro.status.slice(1)}
                    </span>
                  </div>
                </td>
                <td style={{ padding: "0 12px", fontSize: 13, color: "#64748B", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {relativeTime(pro.lastActive)}
                </td>
                <td style={{ padding: "0 12px", textAlign: "right" }} onClick={e => e.stopPropagation()}>
                  <div className="dropdown-container" style={{ position: "relative", display: "inline-block" }}>
                    <button
                      onClick={() => setOpenDropdownId(openDropdownId === pro.id ? null : pro.id)}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        border: "1px solid #E2E8F0",
                        background: "#fff",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                        <circle cx="12" cy="12" r="1" />
                        <circle cx="12" cy="5" r="1" />
                        <circle cx="12" cy="19" r="1" />
                      </svg>
                    </button>
                    {openDropdownId === pro.id && (
                      <div style={{
                        position: "absolute",
                        right: 0,
                        top: "calc(100% + 4px)",
                        width: 180,
                        background: "#fff",
                        border: "1px solid #E2E8F0",
                        borderRadius: 10,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        zIndex: 99,
                        overflow: "hidden"
                      }}>
                        <DropdownItem label="Approve" onClick={() => {
                          onApprovePro(pro.id);
                          setOpenDropdownId(null);
                        }} />
                        <DropdownItem label="Message" onClick={() => {
                          onMessagePro(pro.id);
                          setOpenDropdownId(null);
                        }} />
                        <DropdownItem label="View profile" onClick={() => {
                          onRowClick(pro);
                          setOpenDropdownId(null);
                        }} />
                        <DropdownItem label="Trigger payout review" onClick={() => {
                          onPayoutReview(pro.id);
                          setOpenDropdownId(null);
                        }} />
                        <DropdownItem label="Suspend" onClick={() => {
                          onSuspendPro(pro.id);
                          setOpenDropdownId(null);
                        }} danger />
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{
          marginTop: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #F1F5F9"
        }}>
          <div style={{ fontSize: 13, color: "#64748B", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Showing {startIndex}–{endIndex} of {allPros.length}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                border: "1px solid #E2E8F0",
                background: "#fff",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                opacity: currentPage === 1 ? 0.5 : 1
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    border: currentPage === pageNum ? "none" : "1px solid #E2E8F0",
                    background: currentPage === pageNum ? "#0F172A" : "#fff",
                    color: currentPage === pageNum ? "#fff" : "#64748B",
                    cursor: "pointer",
                    fontWeight: 500,
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                border: "1px solid #E2E8F0",
                background: "#fff",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                opacity: currentPage === totalPages ? 0.5 : 1
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}